import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { FORMA_PAGO, REGIMEN_FISCAL, USO_CFDI } from "@uirtus/shared";
import { useState } from "react";
import { FormularioCliente } from "../componentes/formulario-cliente";
import { FormularioContacto } from "../componentes/formulario-contacto";
import { Pencil, Trash2 } from "lucide-react";
import {
  Aviso,
  Boton,
  BotonIcono,
  Confirmacion,
  Etiqueta,
  Modal,
  Tarjeta,
} from "../componentes/ui";
import { ErrorApi } from "../lib/api";
import {
  useActualizarCliente,
  useCambiarActivo,
  useCliente,
  useCrearContacto,
  useActualizarContacto,
  useEliminarCliente,
  useEliminarContacto,
  useRevisarListaNegra,
  type Contacto,
} from "../lib/clientes";

export function ClienteDetalle() {
  const { id } = useParams({ from: "/clientes/$id" });
  const navegar = useNavigate();

  const { data: cliente, isLoading, isError, error } = useCliente(id);

  const [editando, setEditando] = useState(false);
  const [confirmarBaja, setConfirmarBaja] = useState(false);
  const [contactoEnEdicion, setContactoEnEdicion] = useState<Contacto | null>(null);
  const [contactoNuevo, setContactoNuevo] = useState(false);
  const [contactoABorrar, setContactoABorrar] = useState<Contacto | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);

  const actualizar = useActualizarCliente(id);
  const cambiarActivo = useCambiarActivo(id);
  const eliminar = useEliminarCliente(id);
  const revisarListaNegra = useRevisarListaNegra(id);
  const crearContacto = useCrearContacto(id);
  const actualizarContacto = useActualizarContacto();
  const eliminarContacto = useEliminarContacto();

  if (isLoading) return <p className="text-sm text-slate-500">Cargando…</p>;

  if (isError || !cliente) {
    return (
      <div className="space-y-4">
        <Aviso>
          {error instanceof ErrorApi ? error.message : "No se pudo cargar el cliente"}
        </Aviso>
        <Link to="/clientes" className="text-sm text-marca-600 hover:underline">
          ← Volver a clientes
        </Link>
      </div>
    );
  }

  const totalUso =
    cliente.uso.facturas + cliente.uso.proyectos + cliente.uso.cotizaciones;
  const sePuedeBorrar = totalUso === 0;

  return (
    <div className="space-y-5">
      <Link to="/clientes" className="text-sm text-marca-600 hover:underline">
        ← Clientes
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold">{cliente.razonSocial}</h1>
            {!cliente.activo && <Etiqueta>Inactivo</Etiqueta>}
            {cliente.enListaNegra && <Etiqueta tono="peligro">Lista 69-B</Etiqueta>}
          </div>
          <p className="font-mono text-sm text-slate-500">{cliente.rfc}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Boton variante="secundario" onClick={() => setEditando(true)}>
            Editar
          </Boton>
          <Boton
            variante="secundario"
            disabled={revisarListaNegra.isPending}
            onClick={() =>
              revisarListaNegra.mutate(undefined, {
                onSuccess: (r) => setMensaje(r.mensaje),
                onError: (e) => setMensaje(e.message),
              })
            }
          >
            {revisarListaNegra.isPending ? "Consultando…" : "Revisar 69-B"}
          </Boton>
          <Boton
            variante="secundario"
            onClick={() => cambiarActivo.mutate(!cliente.activo)}
            disabled={cambiarActivo.isPending}
          >
            {cliente.activo ? "Desactivar" : "Reactivar"}
          </Boton>
          <Boton
            variante="peligro"
            onClick={() => setConfirmarBaja(true)}
            disabled={!sePuedeBorrar}
            title={
              sePuedeBorrar
                ? undefined
                : "Tiene historial: solo se puede desactivar"
            }
          >
            Eliminar
          </Boton>
        </div>
      </header>

      {mensaje && <Aviso tono="alerta">{mensaje}</Aviso>}

      {cliente.enListaNegra && (
        <Aviso>
          Este RFC aparece en la lista del artículo 69-B del SAT. Facturarle
          tiene consecuencias fiscales: revisa la situación antes de emitir.
        </Aviso>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <Tarjeta titulo="Datos fiscales">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <Dato etiqueta="Régimen">
              {cliente.regimenFiscal} · {REGIMEN_FISCAL[cliente.regimenFiscal]}
            </Dato>
            <Dato etiqueta="Código postal">
              <span className="font-mono">{cliente.codigoPostal}</span>
            </Dato>
            <Dato etiqueta="Nombre comercial">{cliente.nombreComercial ?? "—"}</Dato>
            <Dato etiqueta="Uso de CFDI">
              {cliente.usoCfdiDefault
                ? `${cliente.usoCfdiDefault} · ${USO_CFDI[cliente.usoCfdiDefault]}`
                : "Sin preferencia"}
            </Dato>
            <Dato etiqueta="Forma de pago">
              {cliente.formaPagoDefault
                ? `${cliente.formaPagoDefault} · ${FORMA_PAGO[cliente.formaPagoDefault]}`
                : "Sin preferencia"}
            </Dato>
            <Dato etiqueta="Crédito">
              {cliente.diasCredito > 0 ? `${cliente.diasCredito} días` : "Contado"}
            </Dato>
            <Dato etiqueta="Correo">{cliente.email ?? "—"}</Dato>
            <Dato etiqueta="Teléfono">{cliente.telefono ?? "—"}</Dato>
            <Dato etiqueta="Sitio">{cliente.sitioWeb ?? "—"}</Dato>
          </dl>

          {cliente.notas && (
            <p className="mt-4 whitespace-pre-wrap border-t border-slate-200 pt-3 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-300">
              {cliente.notas}
            </p>
          )}
        </Tarjeta>

        <div className="space-y-5">
          <Tarjeta
            titulo="Contactos"
            acciones={
              <Boton variante="secundario" onClick={() => setContactoNuevo(true)}>
                Agregar
              </Boton>
            }
          >
            {cliente.contactos.length === 0 ? (
              <p className="py-4 text-center text-sm text-slate-500">
                Sin contactos registrados.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {cliente.contactos.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{c.nombre}</span>
                        {c.esPrincipal && <Etiqueta tono="exito">Principal</Etiqueta>}
                      </div>
                      {c.puesto && (
                        <div className="text-xs text-slate-500">{c.puesto}</div>
                      )}
                      <div className="truncate text-xs text-slate-500">
                        {[c.email, c.telefono].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-0.5">
                      <BotonIcono
                        etiqueta={`Editar a ${c.nombre}`}
                        onClick={() => setContactoEnEdicion(c)}
                      >
                        <Pencil size={16} />
                      </BotonIcono>
                      <BotonIcono
                        etiqueta={`Borrar a ${c.nombre}`}
                        variante="peligro"
                        onClick={() => setContactoABorrar(c)}
                      >
                        <Trash2 size={16} />
                      </BotonIcono>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Tarjeta>

          <Tarjeta titulo="Historial">
            <div className="grid grid-cols-3 gap-3 text-center">
              <Contador n={cliente.uso.facturas} etiqueta="Facturas" />
              <Contador n={cliente.uso.cotizaciones} etiqueta="Cotizaciones" />
              <Contador n={cliente.uso.proyectos} etiqueta="Proyectos" />
            </div>
            {!sePuedeBorrar && (
              <p className="mt-4 border-t border-slate-200 pt-3 text-xs text-slate-500 dark:border-slate-800">
                Con historial el cliente ya no se borra, solo se desactiva. Las
                facturas emitidas conservan sus propios datos fiscales, así que
                editarlo aquí no altera nada de lo ya timbrado.
              </p>
            )}
          </Tarjeta>
        </div>
      </div>

      {/* --- Modales --- */}

      <Modal
        titulo="Editar cliente"
        abierto={editando}
        alCerrar={() => {
          setEditando(false);
          actualizar.reset();
        }}
      >
        <FormularioCliente
          cliente={cliente}
          enviando={actualizar.isPending}
          errorServidor={actualizar.error ? actualizar.error.message : null}
          alEnviar={(datos) =>
            actualizar.mutate(datos as never, {
              onSuccess: () => {
                setEditando(false);
                actualizar.reset();
              },
            })
          }
          alCancelar={() => {
            setEditando(false);
            actualizar.reset();
          }}
        />
      </Modal>

      <Modal
        titulo="Nuevo contacto"
        abierto={contactoNuevo}
        alCerrar={() => setContactoNuevo(false)}
        ancho="max-w-lg"
      >
        <FormularioContacto
          enviando={crearContacto.isPending}
          errorServidor={crearContacto.error ? crearContacto.error.message : null}
          alEnviar={(datos) =>
            crearContacto.mutate(datos as never, {
              onSuccess: () => setContactoNuevo(false),
            })
          }
          alCancelar={() => setContactoNuevo(false)}
        />
      </Modal>

      <Modal
        titulo="Editar contacto"
        abierto={!!contactoEnEdicion}
        alCerrar={() => setContactoEnEdicion(null)}
        ancho="max-w-lg"
      >
        {contactoEnEdicion && (
          <FormularioContacto
            contacto={contactoEnEdicion}
            enviando={actualizarContacto.isPending}
            errorServidor={
              actualizarContacto.error ? actualizarContacto.error.message : null
            }
            alEnviar={(datos) =>
              actualizarContacto.mutate(
                { id: contactoEnEdicion.id, ...datos } as never,
                { onSuccess: () => setContactoEnEdicion(null) },
              )
            }
            alCancelar={() => setContactoEnEdicion(null)}
          />
        )}
      </Modal>

      <Confirmacion
        abierto={!!contactoABorrar}
        titulo="Borrar contacto"
        mensaje={`Se va a borrar a ${contactoABorrar?.nombre}. Esta acción no se puede deshacer.`}
        textoConfirmar="Borrar"
        peligroso
        ocupado={eliminarContacto.isPending}
        alCancelar={() => setContactoABorrar(null)}
        alConfirmar={() =>
          contactoABorrar &&
          eliminarContacto.mutate(contactoABorrar.id, {
            onSuccess: () => setContactoABorrar(null),
          })
        }
      />

      <Confirmacion
        abierto={confirmarBaja}
        titulo="Eliminar cliente"
        mensaje={
          <>
            Se va a borrar <strong>{cliente.razonSocial}</strong> de forma
            definitiva. Solo es posible porque no tiene facturas, cotizaciones ni
            proyectos.
          </>
        }
        textoConfirmar="Eliminar"
        peligroso
        ocupado={eliminar.isPending}
        alCancelar={() => setConfirmarBaja(false)}
        alConfirmar={() =>
          eliminar.mutate(undefined, {
            onSuccess: () => void navegar({ to: "/clientes" }),
            onError: (e) => {
              setConfirmarBaja(false);
              setMensaje(e.message);
            },
          })
        }
      />
    </div>
  );
}

function Dato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-slate-500">{etiqueta}</dt>
      <dd className="text-right">{children}</dd>
    </>
  );
}

function Contador({ n, etiqueta }: { n: number; etiqueta: string }) {
  return (
    <div>
      <div className="text-2xl font-semibold">{n}</div>
      <div className="text-xs text-slate-500">{etiqueta}</div>
    </div>
  );
}
